import React, { useState, useEffect, useCallback } from 'react';
import { memberService } from '../../services/member.service';
import type { FamilyMember } from '../../types/member';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Modal } from '../common/Modal';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

interface FamilyInfoProps {
  memberId: string;
}

// 간단한 가족 추가 폼
const FamilyMemberForm: React.FC<{ onSubmit: (data: Omit<FamilyMember, 'id'>) => void; }> = ({ onSubmit }) => {
  const [relationship, setRelationship] = useState('');
  const [name, setName] = useState('');
  const [believer, setBeliever] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!relationship || !name) {
      alert('관계와 이름은 필수 항목입니다.');
      return;
    }
    onSubmit({ relationship, name, believer });
    setRelationship('');
    setName('');
    setBeliever(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">관계</label>
        <input
          type="text"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm dark:bg-gray-700 dark:border-gray-600"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">이름</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm dark:bg-gray-700 dark:border-gray-600"
          required
        />
      </div>
      <div className="flex items-center">
        <input
          id="believer"
          type="checkbox"
          checked={believer}
          onChange={(e) => setBeliever(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor="believer" className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
          동반신앙
        </label>
      </div>
      <div className="flex justify-end">
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          추가
        </button>
      </div>
    </form>
  );
};


const FamilyInfo: React.FC<FamilyInfoProps> = ({ memberId }) => {
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchFamilyMembers = useCallback(async () => {
    if (!memberId) return;
    try {
      setLoading(true);
      const members = await memberService.getFamilyMembers(memberId);
      setFamilyMembers(members);
    } catch (err) {
      setError('가족 정보를 불러오는 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchFamilyMembers();
  }, [fetchFamilyMembers]);

  const handleAddFamilyMember = async (data: Omit<FamilyMember, 'id'>) => {
    try {
      await memberService.addFamilyMember(memberId, data);
      setIsModalOpen(false);
      await fetchFamilyMembers(); // 목록 새로고침
    } catch (error) {
      alert('가족 구성원 추가에 실패했습니다.');
      console.error(error);
    }
  };

  const handleRemoveFamilyMember = async (familyMemberId: string) => {
    if (window.confirm('정말로 이 가족 구성원을 삭제하시겠습니까?')) {
      try {
        await memberService.removeFamilyMember(memberId, familyMemberId);
        await fetchFamilyMembers(); // 목록 새로고침
      } catch (error) {
        alert('가족 구성원 삭제에 실패했습니다.');
        console.error(error);
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold leading-7 text-gray-900">가족사항</h3>
        <button onClick={() => setIsModalOpen(true)} className="no-print px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm">
          가족 추가
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">관계</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">이름</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">동반신앙</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {familyMembers.length > 0 ? (
              familyMembers.map((person) => (
                <tr key={person.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">{person.relationship}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{person.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{person.believer ? 'Y' : 'N'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <div className="flex justify-center space-x-4">
                      <button onClick={() => alert('수정 기능은 곧 구현됩니다.')} className="text-gray-400 hover:text-blue-500 transition-colors">
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button onClick={() => handleRemoveFamilyMember(person.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                  등록된 가족 정보가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="가족 구성원 추가">
        <FamilyMemberForm onSubmit={handleAddFamilyMember} />
      </Modal>
    </div>
  );
};

export default FamilyInfo;
